import { FC, useContext, useEffect } from "react";
import { Controller } from "react-hook-form";
import { useAuthContext } from "../../context/authContext";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useI18n from "../../hooks/useTranslations";
import { useProjectsApi } from "../../http/useProjects";
import { getUILanguages, Language } from "../../types";
import Button from "../ui/Button";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";

import Textarea from "../ui/Textarea";
import { CustomSelect } from "../ui/select/CustomSelect";

interface InsertProjectFormBody {
  name?: string;
  language?: Language;
  description?: string;
}

interface CreateProjectFormProps {
  onSubmit: (projectId: string) => void;
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
  closeModal: () => void;
}

const CreateProjectForm: FC<CreateProjectFormProps> = ({
  onSubmit,
  isSubmitting,
  setIsSubmitting,
  closeModal,
}: CreateProjectFormProps) => {
  const i18n = useI18n();
  const auth = useAuthContext();
  const dispatch = useContext(NotificationDispatch);

  const projectsApi = useProjectsApi();

  const languages = getUILanguages(i18n.translation);

  const submit = () => async (body: InsertProjectFormBody) => {
    setIsSubmitting(true);
    try {
      const res = await projectsApi.insertProject({
        name: body.name!,
        description: body.description,
        language: body.language?.value!,
      });
      return onSubmit(res.id);
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const defaultValues = {
    name: i18n.translation.projects.new,
    language: languages.find((x) => x.value === auth.identity?.language),
  };

  return (
    <Form<InsertProjectFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.create}
      secondaryButton={
        <Button buttonProps={{ onClick: () => closeModal() }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      disabled={isSubmitting}
      onSubmit={submit}
    >
      {({ register, formState: { errors }, control, setValue, reset }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [i18n]);

        return (
          <FormFieldsContainer fullWidth>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isOptional, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  {...register("name", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <Controller
              name="language"
              control={control}
              rules={{ required: true }}
              defaultValue={defaultValues.language}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.languages.singular} error={fieldState.error}>
                  {({ labelId }) => (
                    <CustomSelect
                      {...field}
                      inputId={labelId}
                      options={languages}
                      getOptionLabel={(x) => x.display}
                      onChange={(value) => setValue("language", value ?? undefined)}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField label={i18n.translation.common.description} error={errors.description} isOptional>
              {({ labelId, isOptional, isInvalid }) => (
                <Textarea id={labelId} isInvalid={isInvalid} {...register("description", { required: !isOptional })} />
              )}
            </FormField>
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default CreateProjectForm;
